import { createContext, useCallback, useMemo, useState } from "react";

export type LoadingContextProps = {
  isLoading: boolean;
  /**
   * Liga/desliga o overlay de forma imperativa. Serve para os fluxos que controlam o ciclo
   * inteiro dentro de uma promise (`try/finally`), onde o desligamento não depende do
   * componente continuar montado.
   */
  setLoading: (isLoading: boolean) => void;
  /**
   * Reserva o overlay e devolve a função que o libera.
   *
   * <p>É o que os hooks de requisição usam: a liberação vai no cleanup de um `useEffect`,
   * então o React garante que ela roda mesmo se o componente desmontar no meio da
   * requisição. Enquanto o desligamento dependia do callback `onSettled` do react-query,
   * sair da tela antes da resposta deixava o overlay ligado para sempre — ele é
   * `position: fixed`, cobre a viewport inteira com `z-index: 1000000` e engole todo
   * clique, então o sistema inteiro ficava travado até recarregar a página.</p>
   *
   * <p>As reservas são contadas: com várias requisições simultâneas, o overlay só some
   * quando a última terminar.</p>
   */
  reservarCarregamento: () => () => void;
};

export const LoadingContext = createContext<LoadingContextProps>({
  isLoading: false,
  setLoading: () => {},
  reservarCarregamento: () => () => {},
});

export const LoadingContextProvider = ({ children }: any) => {
  const [carregamentoManual, setCarregamentoManual] = useState(false);
  const [reservas, setReservas] = useState(0);

  const reservarCarregamento = useCallback(() => {
    setReservas((total) => total + 1);

    let liberada = false;
    return () => {
      if (liberada) return;
      liberada = true;
      setReservas((total) => Math.max(0, total - 1));
    };
  }, []);

  const setLoading = useCallback((isLoading: boolean) => setCarregamentoManual(isLoading), []);

  const valor = useMemo(
    () => ({
      isLoading: carregamentoManual || reservas > 0,
      setLoading,
      reservarCarregamento,
    }),
    [carregamentoManual, reservas, setLoading, reservarCarregamento]
  );

  return <LoadingContext.Provider value={valor}>{children}</LoadingContext.Provider>;
};
